import { Fragment, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { Box, Group, Stack, Text, Title, Anchor, Paper, Progress, Image, Alert } from "@mantine/core";
import { getTask, getTaskEvents, getChannels, getSubjects, getVideoUrl, updateLecture, setTaskLecture, useFetch, LecturePatch, TASK_STATE_COMPLETED, TASK_STATE_FAILED, TASK_STATE_PENDING, TASK_STATE_RUNNING, PLATFORM_YOUTUBE, PLATFORM_VK, playlistUrl } from "../../shared/api";
import { useAuth } from "../../shared/AuthContext";
import { ErrorDisplay, Loading, formatDateTime, usePageTitle } from "../../shared/utils";
import { InlineRow } from "../../shared/components/InlineRow";
import { InlineSelect } from "../../shared/components/InlineSelect";
import { StatusIcon } from "./StatusIcon";
import youtubeIcon from "../../shared/assets/colored/youtube.png";
import vkIcon from "../../shared/assets/colored/vk.png";

const statusLabels: Record<string, string> = { [TASK_STATE_COMPLETED]: 'Успешно', [TASK_STATE_RUNNING]: 'В процессе', [TASK_STATE_PENDING]: 'Ожидание', [TASK_STATE_FAILED]: 'Ошибка' };
const platformIcons: Record<string, string> = { [PLATFORM_YOUTUBE]: youtubeIcon, [PLATFORM_VK]: vkIcon };

function useAuthenticatedUrl(url: string | null): string | null {
  const auth = useAuth();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!url) return;
    let objectUrl: string;
    auth.identity().then(({ token }) => fetch(url, { headers: { authorization: `Bearer ${token}` } }))
      .then(rsp => rsp.blob()).then(blob => { objectUrl = URL.createObjectURL(blob); setBlobUrl(objectUrl); });
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url, auth]);
  return blobUrl;
}

function TaskEvents({ task_id }: { task_id: string }) {
  const { fetch } = useFetch();
  const { data: events, error, isLoading } = useSWR({ task_id, limit: 20, fetch }, getTaskEvents, { refreshInterval: 5000 });
  if (error) return <ErrorDisplay error={error} />;
  if (isLoading || !events) return <Text fz="sm" c="dimmed">Загрузка событий...</Text>;
  return (
    <Stack gap="xs">
      {events.map(e => <Text key={e.id} fz="sm" c="dimmed"><Text span c="dimmed" opacity={0.7}>{formatDateTime(e.timestamp)}</Text> {e.body}</Text>)}
    </Stack>
  );
}

export function TaskDetails() {
  const { task_id } = useParams();
  if (!task_id) throw new Error('Missing parameter: task_id');
  usePageTitle(`Task ${task_id.slice(0, 8)}-...`);
  const { fetch } = useFetch();
  const { data: task, error, isLoading, mutate } = useSWR({ task_id, fetch }, getTask, { refreshInterval: 5000 });
  const { data: channels } = useSWR({ fetch }, getChannels);
  const { data: subjects } = useSWR("admin/subjects", () => getSubjects({ fetch }));
  const [lectureError, setLectureError] = useState<string | null>(null);
  // A task published without a lecture gets one once both the subject and the number are picked.
  const [draft, setDraft] = useState<{ subjectId: number | null; number: number | null }>({ subjectId: null, number: null });

  async function handleLectureChange(patch: LecturePatch) {
    if (task?.lecture_id == null) return;
    setLectureError(null);
    try { await updateLecture({ id: task.lecture_id, patch, fetch }); await mutate(); }
    catch (e: any) { setLectureError(e?.message ?? String(e)); }
  }

  async function handleDraftChange(next: typeof draft) {
    setDraft(next);
    if (next.subjectId == null || next.number == null) return;
    setLectureError(null);
    try { await mutate(await setTaskLecture({ taskId: task_id!, subjectId: next.subjectId, number: next.number, fetch }), { revalidate: false }); }
    catch (e: any) { setLectureError(e?.message ?? String(e)); }
  }

  const thumbUrl = useAuthenticatedUrl(task?.source_thumbnail_path ? `/api/files/${task.source_share}/${task.source_thumbnail_path.replace(/^\//, '').split('/').map(encodeURIComponent).join('/')}` : null);
  const playlistName = useMemo(() => {
    if (!channels || !task) return task?.target_playlist_ref || '';
    return [...channels.youtube, ...channels.vk].find(c => c.id === task.target_channel_ref)?.playlists.find(p => p.playlist_id === task.target_playlist_ref)?.title || task.target_playlist_ref;
  }, [channels, task]);

  if (error) return <ErrorDisplay error={error} />;
  if (isLoading || !task) return <Loading />;

  const pUrl = task.target_playlist_ref ? playlistUrl(task.target_platform, task.target_channel_ref, task.target_playlist_ref) : '';;
  const vUrl = task.result_id ? getVideoUrl(task.target_platform, task.result_id, task.target_channel_ref) : null;
  const hasLecture = task.lecture_id != null;
  const subjectId = hasLecture ? task.lecture_subject_id : draft.subjectId;
  const lectureNumber = hasLecture ? task.lecture_number : draft.number;
  const headerExtras = [
    vUrl && <Anchor href={vUrl} target="_blank" rel="noopener noreferrer" underline="never">Открыть видео</Anchor>,
    hasLecture && task.subject_title && <Text>{task.subject_title}</Text>,
    hasLecture && task.lecture_number != null && <Text style={{ whiteSpace: 'nowrap' }}>№{task.lecture_number}</Text>,
  ].filter(Boolean);

  return (
    <Box component="main" p="xl">
      <Group align="flex-start" gap={32} wrap="wrap">
        <Box style={{ flex: '1 1 400px', minWidth: 300 }}>
          <Group gap="sm" align="center" mb={task.state === 'running' && task.progress != null ? 'sm' : 'lg'}>
            <StatusIcon state={task.state} size={24} />
            <Text fz="lg" fw={500}>{statusLabels[task.state] || task.state}</Text>
            {headerExtras.map((el, i) => <Fragment key={i}><Text c="dimmed">•</Text>{el}</Fragment>)}
          </Group>
          {task.state === 'running' && task.progress != null && (
            <Box mb="lg"><Progress value={task.progress} size="sm" radius="xl" /><Text fz="xs" c="dimmed" mt={4}>{task.progress}%</Text></Box>
          )}
          <Group gap="xs" align="center" mb="sm">
            <Title order={2} fz={28}>{task.target_title}</Title>
            {platformIcons[task.target_platform] && <img src={platformIcons[task.target_platform]} alt="" style={{ height: 'auto', width: 28 }} />}
          </Group>
          <Text c="dimmed" mb="sm">{task.created_by}</Text>
          <Text mb="md"><strong>Плейлист:</strong> {!task.target_playlist_ref ? <Text span c="dimmed">без плейлиста</Text> : pUrl ? <Anchor href={pUrl} target="_blank">{playlistName}</Anchor> : playlistName}</Text>
          <Stack gap={0} mb="md">
            <InlineSelect label="Предмет" labelWidth={120} searchable value={subjectId != null ? String(subjectId) : null} data={(subjects ?? []).map(s => ({ value: String(s.id), label: s.title }))}
              onChange={val => { const id = val ? Number(val) : null; if (hasLecture) handleLectureChange({ subject_id: id }); else handleDraftChange({ ...draft, subjectId: id }); }} />
            <InlineRow label="Номер лекции" labelWidth={120} placeholder="Не указан" value={lectureNumber != null ? String(lectureNumber) : null}
              onValueChange={val => { const n = parseFloat(val.replace(',', '.')); if (isNaN(n)) return; if (hasLecture) handleLectureChange({ number: n }); else handleDraftChange({ ...draft, number: n }); }} />
            {!hasLecture && <Text fz="xs" c="dimmed" mt={4}>Задача опубликована без лекции. Укажите предмет и номер — задача привяжется к лекции. ЧРА при этом не начисляются, их можно добавить вручную.</Text>}
          </Stack>
          {lectureError && <Alert color="red" mb="md" onClose={() => setLectureError(null)} withCloseButton>{lectureError}</Alert>}
          <Paper withBorder radius="sm" p="md" bg="var(--mantine-color-default)"><Text fz="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>{task.target_description}</Text></Paper>
        </Box>
        <Box style={{ flex: '0 0 300px', minWidth: 250 }}>
          {thumbUrl ? <Image src={thumbUrl} radius="sm" mb="lg" style={{ border: '1px solid var(--mantine-color-default-border)' }} /> : <Paper withBorder radius="sm" p="md" mb="lg" ta="center" bg="var(--mantine-color-default)"><Text fz="sm" c="dimmed">Предпросмотр недоступен</Text></Paper>}
          {[["Создано", formatDateTime(task.created_at)], ["Канал", task.target_channel_ref], ["Публичное", task.target_public ? "Да" : "Нет"], ["Источник", task.source_share], ["Путь", task.source_path]].map(([l, v]) => (
            <Box mb="md" key={l}><Text fz="sm" fw={500} mb={4}>{l}</Text><Text fz="sm" c="dimmed" style={{ wordBreak: 'break-all' }}>{v}</Text></Box>
          ))}
        </Box>
      </Group>
      <Box mt="xl"><Text fz="sm" fw={500} mb="xs">События</Text><Paper withBorder radius="sm" p="md" bg="var(--mantine-color-default)"><TaskEvents task_id={task_id} /></Paper></Box>
    </Box>
  );
}
